import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { ReportContextSchema, contextCarriesRef } from '../report-context/report-context';
import { ReportReasonCodeSchema, requiresReportNote } from '../report-reason/report-reason';

/** How much the reporter may write. Long enough for what happened, not an essay. */
export const REPORT_NOTE_MAX_LENGTH = 2000;

/**
 * Filing a report about another member.
 *
 * The reporter is the token and the clock is the server's (N3) — a client that
 * could name either would be a client that could report *as* somebody else.
 *
 * A **blank** note normalizes to `null` rather than `''`, for the reason
 * `ReviewMatchSchema` gives: an empty quotation under "the reporter said" reads
 * as a redaction rather than as an absence.
 *
 * Two cross-field rules, both expressed here rather than in the service, so the
 * client and the api refuse the same requests and the refusal arrives as a
 * **422** from the `ZodValidationPipe` with the offending field named (P18a).
 * Both delegate to the predicate that owns the fact, so neither restates an enum:
 *
 * 1. **`other` needs a note** ({@link requiresReportNote}). A catch-all with no
 *    words is the one report a reviewer cannot action at all, and finding that
 *    out in the queue wastes the reviewer's time and the reporter's courage.
 * 2. **A ref belongs to exactly the contexts that can carry one**
 *    ({@link contextCarriesRef}) — present for a conversation, absent otherwise.
 *    Requiring it stops a chat report arriving with nothing to open; forbidding
 *    it elsewhere stops a client attaching an id whose meaning nobody defined.
 *    Whether the ref names a conversation the *reporter* is in is a question
 *    only the server can answer, and it answers it with
 *    `moderation_report_context_ref_unusable`.
 */
export const ReportCreateSchema = z
  .object({
    reportedUserId: UuidSchema,
    reason: ReportReasonCodeSchema,
    context: ReportContextSchema,
    /** The conversation this was raised from. Present only for `conversation`. */
    contextRef: UuidSchema.nullable().default(null),
    note: z
      .string()
      .trim()
      .max(REPORT_NOTE_MAX_LENGTH)
      .nullable()
      .default(null)
      .transform((note) => (note === null || note === '' ? null : note)),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (requiresReportNote(value.reason) && value.note === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'A note is required for this reason.',
      });
    }

    const expectsRef = contextCarriesRef(value.context);

    if (expectsRef && value.contextRef === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['contextRef'],
        message: 'This context requires the id it was raised from.',
      });
    }

    if (!expectsRef && value.contextRef !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['contextRef'],
        message: 'This context carries no reference.',
      });
    }
  });

export type ReportCreateRequest = z.infer<typeof ReportCreateSchema>;
